import React from "react";
import { Menu, MenuButton, MenuItems, MenuItem } from "@headlessui/react";
import { SvgIcon } from "@/svg";
import clsx from "clsx";
import { CheckIcon } from "@/components/CheckIcon";

export type DropdownOption<T> = {
  id: T;
  label: string;
  icon?: React.ReactNode;
  disabled?: boolean;
  metadata?: React.ReactNode;
};

export type CustomTriggerProps<T> = {
  selected: DropdownOption<T> | undefined;
};

export type CustomOptionProps<T> = {
  option: DropdownOption<T>;
  isSelected: boolean;
  onSelectOption: () => void;
};

export type DropdownProps<T> = {
  options: DropdownOption<T>[];
  selectedOption: T;
  onSelect: (option: T) => void;
  label?: string;
  placeholder?: string;
  buttonClassName?: string;
  menuClassName?: string;
  CustomTrigger?: React.ComponentType<CustomTriggerProps<T>>;
  CustomOption?: React.ComponentType<CustomOptionProps<T>>;
  alignment?: "left" | "right";
};

export function Dropdown<T extends string | number>({
  options,
  selectedOption,
  onSelect,
  label,
  placeholder = "Select an option",
  buttonClassName,
  menuClassName,
  CustomTrigger,
  CustomOption,
  alignment = "left",
}: DropdownProps<T>) {
  const selected = options.find((option) => option.id === selectedOption);

  return (
    <Menu as="div" className="relative">
      {CustomTrigger ? (
        <CustomTrigger selected={selected} />
      ) : (
        <MenuButton
          className={clsx(
            "px-4 py-2 text-feintGray text-sm flex items-center gap-2 rounded-lg",
            "focus:outline-none",
            "ui-open:bg-backgroundGray",
            "bg-pureWhite",
            buttonClassName,
          )}
        >
          {label && <span className="text-feintGray">{label}:</span>}
          <span className={clsx(label ? "text-primaryColor" : "text-feintGray")}>
            {selected?.label || placeholder}
          </span>
          <div className="text-feintGray">
            <SvgIcon icon="ChevronDown" />
          </div>
        </MenuButton>
      )}

      <MenuItems
        className={clsx(
          "absolute mt-4 shadow-xl bg-pureWhite flex flex-col rounded-lg p-2 z-10",
          alignment === "right" && "right-0",
          menuClassName || "min-w-48",
        )}
      >
        {options.map((option) => {
          const isSelected = option.id === selectedOption;
          const handleSelect = () => onSelect(option.id);

          if (CustomOption) {
            return React.createElement(
              React.Fragment,
              { key: String(option.id) },
              <CustomOption
                option={option}
                isSelected={isSelected}
                onSelectOption={handleSelect}
              />,
            );
          }

          return (
            <MenuItem
              key={String(option.id)}
              as="button"
              onClick={handleSelect}
              className={clsx(
                "p-2 text-left flex items-center gap-2",
                "hover:text-dark group",
                "ui-active:text-dark",
                "text-feintGray text-sm",
              )}
              disabled={option.disabled}
            >
              {option.icon && (
                <span
                  className={clsx(
                    "text-feintGray ui-active:text-dark group-hover:text-dark",
                    isSelected && "text-dark",
                  )}
                >
                  {option.icon}
                </span>
              )}
              <span className="flex-grow">{option.label}</span>
              {option.metadata}
              <CheckIcon
                visible={isSelected}
                className="text-dark group-hover:opacity-100 ui-active:opacity-100"
              />
            </MenuItem>
          );
        })}
      </MenuItems>
    </Menu>
  );
}
